<template>
  <div>
    <!-- Accès rapide (RGAA) : le header legacy porteur des skip-links est masqué pour les rôles Labricot -->
    <div class="fr-skiplinks">
      <nav class="fr-container" role="navigation" aria-label="Accès rapide">
        <ul class="fr-skiplinks__list no-bullets px-2">
          <li><a class="fr-link mr-2" href="#contenu">Contenu</a></li>
        </ul>
      </nav>
    </div>

    <!-- Barre fine supérieure : hamburger + logo (dégradé lavande repris de l'espace convive) -->
    <v-app-bar app dense height="56" class="labricot-appbar">
      <v-app-bar-nav-icon
        aria-label="Ouvrir le menu de navigation"
        :aria-expanded="String(drawer)"
        @click="drawer = !drawer"
      />
      <v-toolbar-title class="d-flex align-center">
        <img src="/static/images/logo-labricot-new.png" alt="Labricot" height="40" class="labricot-appbar__logo" />
      </v-toolbar-title>
    </v-app-bar>

    <!-- Drawer accordéon à gauche, fermé par défaut, ouverture au hamburger -->
    <v-navigation-drawer v-model="drawer" app temporary left width="280" class="labricot-drawer">
      <v-list nav dense aria-label="Navigation principale">
        <!-- Navigation inter-vues filtrée par rôle. `to` (route SPA) ou `href` (lien hors SPA,
             ex. le tableau de bord Django des questionnaires) : un seul des deux est défini. -->
        <v-list-item
          v-for="item in navItems"
          :key="item.title"
          :to="item.to"
          :href="item.href"
          exact
          link
          @click="drawer = false"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Entrées du gestionnaire portées depuis AppHeader/navLinks (arbitrage S64). Le manager
             est un rôle Labricot : App.vue lui rend AppNavDrawer et jamais AppHeader — les entrées
             vivent donc ici. Groupées en accordéon comme l'étaient les sous-menus d'AppHeader. -->
        <v-list-group v-for="group in navGroups" :key="group.title" :prepend-icon="group.icon" no-action>
          <template #activator>
            <v-list-item-content>
              <v-list-item-title>{{ group.title }}</v-list-item-title>
            </v-list-item-content>
          </template>
          <v-list-item
            v-for="child in group.children"
            :key="child.title"
            :to="child.to"
            :href="child.href"
            exact
            link
            @click="drawer = false"
          >
            <v-list-item-content>
              <v-list-item-title>{{ child.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>

        <v-divider class="my-2" />

        <!-- Informations légales (accordéon) -->
        <v-list-group prepend-icon="mdi-information-outline" no-action>
          <template #activator>
            <v-list-item-content>
              <v-list-item-title>Informations légales</v-list-item-title>
            </v-list-item-content>
          </template>
          <!-- La cle est le nom de route, pas l'objet `to` : Vue refuse une cle non primitive et
               reserialise la liste a chaque rendu. Le nom est unique par construction. -->
          <v-list-item v-for="link in legalLinks" :key="link.to.name" :to="link.to" exact link @click="drawer = false">
            <v-list-item-content>
              <v-list-item-title>{{ link.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>

        <v-divider class="my-2" />

        <!-- Déconnexion -->
        <v-list-item link @click="confirmLogout = true">
          <v-list-item-icon>
            <v-icon>mdi-logout</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Déconnexion</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Confirmation de déconnexion -->
    <v-dialog v-model="confirmLogout" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Se déconnecter ?</v-card-title>
        <v-card-text>Vous allez être déconnecté de votre session Labricot.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmLogout = false">Annuler</v-btn>
          <v-btn color="primary" @click="logout">Se déconnecter</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { buildNavItems, buildNavGroups, LEGAL_LINKS } from "@/navigation"

export default {
  name: "AppNavDrawer",

  data() {
    return {
      drawer: false,
      confirmLogout: false,
      // Habilitation pédagogique : elle est portée par le rattachement à un établissement, pas
      // par le rôle. Le drawer la demande au serveur plutôt que de la déduire de `SUPERVISOR`,
      // sinon il proposerait un atelier vide à tout superviseur.
      isPedagogicalLead: false,
    }
  },

  mounted() {
    this.loadPedagogicalLead()
  },

  watch: {
    // `loggedUser` arrive après le montage : sans ce rappel, un superviseur n'aurait le rôle
    // qu'une fois le drawer déjà construit, et l'entrée n'apparaîtrait qu'au rechargement.
    userRoles: {
      handler() {
        this.loadPedagogicalLead()
      },
    },
  },

  computed: {
    // Rôles effectifs (rattachements + professions hors pivot) : la navigation suit les
    // droits réels, pas la profession déclarée dans le profil.
    userRoles() {
      return this.$store.getters.userRoles
    },

    contextCanteen() {
      return this.$store.getters.contextCanteen
    },

    // Navigation : construite par la config partagée `@/navigation` (source unique consommée par
    // tous les rôles depuis « tout burger partout »). Le composant ne fait que fournir l'état
    // (rôles, cantine de contexte, habilitation pédagogique) et rendre le résultat.
    navItems() {
      return buildNavItems({
        userRoles: this.userRoles,
        contextCanteen: this.contextCanteen,
        isPedagogicalLead: this.isPedagogicalLead,
      })
    },

    navGroups() {
      return buildNavGroups({ userRoles: this.userRoles })
    },

    legalLinks() {
      return LEGAL_LINKS
    },
  },

  methods: {
    /**
     * L'appel n'est tenté que pour un superviseur : les autres rôles n'ont pas d'atelier, et
     * une requête refusée à chaque montage du drawer serait du bruit pur. Un échec laisse
     * l'entrée absente — le reste de la navigation n'en dépend pas.
     */
    loadPedagogicalLead() {
      if (!this.userRoles.includes("SUPERVISOR")) return
      // `Promise.resolve` enveloppe le retour du dispatch : un store sans cette action renvoie
      // `undefined`, et le chaînage direct ferait tomber le montage du composant.
      return Promise.resolve(this.$store.dispatch("fetchPedagogicalCanteens"))
        .then((canteens) => {
          this.isPedagogicalLead = Boolean(canteens && canteens.length)
        })
        .catch(() => {
          this.isPedagogicalLead = false
        })
    },

    logout() {
      this.confirmLogout = false
      this.drawer = false
      this.$store.dispatch("logout")
    },
  },
}
</script>

<style scoped>
/* Dégradé lavande repris du hero de l'espace convive (color prop Vuetify n'accepte pas les dégradés) */
.labricot-appbar >>> .v-toolbar__content {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9ff 60%, #e4dfff 100%);
}
.labricot-appbar__logo {
  display: block;
  max-height: 40px;
  width: auto;
}
</style>
